import React, { useMemo, useState } from "react";
import { ProgressState } from "../../../components/ProgressDialog";
import { useActiveFBOs } from "../../../containers/ActiveFBOContainer";
import { useApi } from "../../../containers/ApiContainer";
import { lookupAircraftByTailNumber } from "../../../hooks/useFAATailNumbers";
import { PlacementService } from "../../../services/PlacementService";
import { TripService } from "../../../services/TripService";
import { TenantType } from "../../../types";
import { isInfinityDate } from "../../../utils/dateHelpers";
import { SimpleBulkUploadPresenter } from "./SimpleBulkUploadPresenter";

interface Trip {
  id: number;
  tail_number: string;
  arrival_date: string;
  departure_date: string;
  aircraft_id?: string;
  make?: string;
  model?: string;
  wingspan?: number;
  length?: number;
}

export interface SimpleBulkUploadRef {
  isFormValid: boolean;
  loading: boolean;
  save: () => Promise<void>;
}

interface SimpleBulkUploadProps {
  open: boolean;
  onClose: () => void;
  onComplete: (count: number) => void;
  // Optional props for auto-assignment to a location
  autoAssignLocationId?: string;
  autoAssignLocationType?: "hangar" | "ramp";
  autoAssignLocationName?: string;
  // Render mode
  embedded?: boolean; // If true, renders without Dialog wrapper
  // State change callback
  onStateChange?: (isValid: boolean, loading: boolean) => void;
}

export const SimpleBulkUpload = React.forwardRef<
  SimpleBulkUploadRef,
  SimpleBulkUploadProps
>(
  (
    {
      open,
      onClose,
      onComplete,
      autoAssignLocationId,
      autoAssignLocationType,
      autoAssignLocationName,
      embedded = false,
      onStateChange,
    },
    ref
  ) => {
    const { activeFBO } = useActiveFBOs();
    const { postgrest } = useApi();

    const [trips, setTrips] = useState<Trip[]>([
      { id: 1, tail_number: "", arrival_date: "", departure_date: "" },
    ]);
    const [pasteData, setPasteData] = useState<string>("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [uploadProgress, setUploadProgress] = useState<ProgressState>({
      current: 0,
      total: 0,
      currentItem: "",
    });
    const [isImporting, setIsImporting] = useState(false);

    // Initialize TripService and PlacementService
    const tripService = useMemo(() => new TripService(postgrest), [postgrest]);
    const placementService = useMemo(
      () => new PlacementService(postgrest),
      [postgrest]
    );

    // Generate example dates for placeholder
    const getExampleDates = useMemo(() => {
      const today = new Date();
      const formatDate = (date: Date) => {
        const hours = date.getHours();
        const minutes = date.getMinutes();
        const ampm = hours >= 12 ? "PM" : "AM";
        const displayHours = hours % 12 || 12;
        return `${
          date.getMonth() + 1
        }/${date.getDate()}/${date.getFullYear()} ${displayHours}:${minutes
          .toString()
          .padStart(2, "0")} ${ampm}`;
      };

      const example1Arrival = new Date(today);
      example1Arrival.setDate(today.getDate() + 3);
      example1Arrival.setHours(10, 11, 0, 0);
      const example1Departure = new Date(example1Arrival);
      example1Departure.setDate(example1Arrival.getDate() + 5);
      example1Departure.setHours(16, 25, 0, 0);

      const example2Arrival = new Date(today);
      example2Arrival.setDate(today.getDate() + 10);
      example2Arrival.setHours(18, 0, 0, 0);
      const example2Departure = new Date(example2Arrival);
      example2Departure.setDate(example2Arrival.getDate() + 4);
      example2Departure.setHours(22, 0, 0, 0);

      const example3Arrival = new Date(today);
      example3Arrival.setDate(today.getDate() + 15);
      example3Arrival.setHours(14, 30, 0, 0);
      const example3Departure = new Date(example3Arrival);
      example3Departure.setDate(example3Arrival.getDate() + 3);
      example3Departure.setHours(16, 45, 0, 0);

      return {
        example1: `N123AB\t${formatDate(example1Arrival)}\t${formatDate(
          example1Departure
        )}`,
        example2: `N456CD\t${formatDate(example2Arrival)}\t${formatDate(
          example2Departure
        )}`,
        example3: `N789EF\t${formatDate(example3Arrival)}\t${formatDate(
          example3Departure
        )}`,
        example4: `N101GH\t${formatDate(example3Arrival)}\tno departure`,
      };
    }, []);

    // Convert Excel date/datetime formats to HTML datetime-local format (YYYY-MM-DDTHH:MM)
    const convertDateFormat = (dateStr: string): string => {
      const trimmed = dateStr.trim();

      // Handle infinity/base tenant formats (empty, "no departure", "infinity", "base tenant")
      if (
        !trimmed ||
        trimmed.toLowerCase() === "no departure" ||
        trimmed.toLowerCase() === "infinity" ||
        trimmed.toLowerCase() === "base tenant"
      ) {
        return "infinity";
      }

      // Check if already in datetime-local format (YYYY-MM-DDTHH:MM)
      if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(trimmed)) {
        return trimmed;
      }

      // Try datetime formats with AM/PM: M/D/YYYY H:MM AM/PM
      let match = trimmed.match(
        /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})\s*(AM|PM)$/i
      );
      if (match) {
        const [, month, day, year, hours, minutes, ampm] = match;
        let hour24 = parseInt(hours, 10);
        if (ampm.toUpperCase() === "PM" && hour24 !== 12) {
          hour24 += 12;
        } else if (ampm.toUpperCase() === "AM" && hour24 === 12) {
          hour24 = 0;
        }
        return `${year}-${month.padStart(2, "0")}-${day.padStart(
          2,
          "0"
        )}T${hour24.toString().padStart(2, "0")}:${minutes}`;
      }

      // Try datetime formats with 24-hour time: M/D/YYYY H:MM or M/D/YYYY HH:MM
      match = trimmed.match(
        /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})$/
      );
      if (match) {
        const [, month, day, year, hours, minutes] = match;
        return `${year}-${month.padStart(2, "0")}-${day.padStart(
          2,
          "0"
        )}T${hours.padStart(2, "0")}:${minutes}`;
      }

      // Try datetime formats with dash separator: M-D-YYYY H:MM AM/PM
      match = trimmed.match(
        /^(\d{1,2})-(\d{1,2})-(\d{4})\s+(\d{1,2}):(\d{2})\s*(AM|PM)$/i
      );
      if (match) {
        const [, month, day, year, hours, minutes, ampm] = match;
        let hour24 = parseInt(hours, 10);
        if (ampm.toUpperCase() === "PM" && hour24 !== 12) {
          hour24 += 12;
        } else if (ampm.toUpperCase() === "AM" && hour24 === 12) {
          hour24 = 0;
        }
        return `${year}-${month.padStart(2, "0")}-${day.padStart(
          2,
          "0"
        )}T${hour24.toString().padStart(2, "0")}:${minutes}`;
      }

      // Try datetime formats with dash separator: M-D-YYYY H:MM or M-D-YYYY HH:MM
      match = trimmed.match(
        /^(\d{1,2})-(\d{1,2})-(\d{4})\s+(\d{1,2}):(\d{2})$/
      );
      if (match) {
        const [, month, day, year, hours, minutes] = match;
        return `${year}-${month.padStart(2, "0")}-${day.padStart(
          2,
          "0"
        )}T${hours.padStart(2, "0")}:${minutes}`;
      }

      // Try date-only formats (backward compatibility) - default to midnight
      // MM/DD/YYYY or M/D/YYYY
      match = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
      if (match) {
        const [, month, day, year] = match;
        return `${year}-${month.padStart(2, "0")}-${day.padStart(
          2,
          "0"
        )}T00:00`;
      }

      // MM-DD-YYYY or M-D-YYYY
      match = trimmed.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
      if (match) {
        const [, month, day, year] = match;
        return `${year}-${month.padStart(2, "0")}-${day.padStart(
          2,
          "0"
        )}T00:00`;
      }

      // YYYY-MM-DD (add default time)
      match = trimmed.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
      if (match) {
        const [, year, month, day] = match;
        return `${year}-${month.padStart(2, "0")}-${day.padStart(
          2,
          "0"
        )}T00:00`;
      }

      // If no format matches, return as is
      return trimmed;
    };

    // Parse TSV data into trip objects
    const parseTSVData = (data: string): Trip[] => {
      const lines = data.trim().split("\n");
      const trips: Trip[] = [];

      lines.forEach((line, index) => {
        const columns = line.split("\t");
        if (columns.length >= 3) {
          const [tailNumber, arrivalDate, departureDate] = columns;

          // Skip empty rows
          if (
            !tailNumber.trim() &&
            !arrivalDate.trim() &&
            !departureDate.trim()
          ) {
            return;
          }

          // Skip header row if detected (check if it contains common header words)
          // NOTE: Must check for exact header patterns, not substrings, to avoid filtering "no departure" as a header
          const lowerTail = tailNumber.toLowerCase();
          const lowerArrival = arrivalDate.toLowerCase();

          // Check if this looks like a header row (all three columns have header-like text)
          const isTailHeader =
            lowerTail.includes("tail") ||
            lowerTail.includes("number") ||
            lowerTail === "n#";
          const isArrivalHeader =
            lowerArrival.includes("arrival") ||
            lowerArrival.includes("date") ||
            lowerArrival === "eta";

          // Only skip if it looks like a full header row (tail AND arrival headers present)
          // Don't filter based on departure alone - "no departure" is valid data, not a header
          if (isTailHeader && isArrivalHeader) {
            return;
          }

          trips.push({
            id: Date.now() + index,
            tail_number: tailNumber.trim(),
            arrival_date: convertDateFormat(arrivalDate),
            departure_date: convertDateFormat(departureDate),
          });
        }
      });

      return trips;
    };

    const handleImportData = async () => {
      if (!pasteData.trim() || !postgrest || !activeFBO) return;

      setIsImporting(true);
      try {
        const parsedTrips = parseTSVData(pasteData);
        if (parsedTrips.length === 0) return;

        // Look up aircraft data for each trip
        const tripsWithAircraft = await Promise.all(
          parsedTrips.map(async (trip) => {
            const aircraft = await lookupAircraftByTailNumber(
              postgrest,
              trip.tail_number,
              activeFBO
            );

            if (aircraft) {
              return {
                ...trip,
                aircraft_id: aircraft.id,
                make: aircraft.make,
                model: aircraft.model,
                wingspan: aircraft.wingspan,
                length: aircraft.length,
              };
            }

            // No aircraft found, return trip without aircraft data
            return trip;
          })
        );

        setTrips(tripsWithAircraft);
        setPasteData(""); // Clear the text area after import
      } finally {
        setIsImporting(false);
      }
    };

    const downloadSampleFile = () => {
      // Generate dates relative to today
      const today = new Date();
      const formatDate = (date: Date) => {
        const hours = date.getHours();
        const minutes = date.getMinutes();
        const ampm = hours >= 12 ? "PM" : "AM";
        const displayHours = hours % 12 || 12;
        return `${
          date.getMonth() + 1
        }/${date.getDate()}/${date.getFullYear()} ${displayHours}:${minutes
          .toString()
          .padStart(2, "0")} ${ampm}`;
      };

      // Create sample trips with dates in the near future
      const trip1Arrival = new Date(today);
      trip1Arrival.setDate(today.getDate() + 3);
      trip1Arrival.setHours(10, 11, 0, 0);
      const trip1Departure = new Date(trip1Arrival);
      trip1Departure.setDate(trip1Arrival.getDate() + 5);
      trip1Departure.setHours(16, 25, 0, 0);

      const trip2Arrival = new Date(today);
      trip2Arrival.setDate(today.getDate() + 10);
      trip2Arrival.setHours(18, 0, 0, 0);
      const trip2Departure = new Date(trip2Arrival);
      trip2Departure.setDate(trip2Arrival.getDate() + 4);
      trip2Departure.setHours(22, 0, 0, 0);

      const trip3Arrival = new Date(today);
      trip3Arrival.setDate(today.getDate() + 20);
      trip3Arrival.setHours(9, 30, 0, 0);
      const trip3Departure = new Date(trip3Arrival);
      trip3Departure.setDate(trip3Arrival.getDate() + 3);
      trip3Departure.setHours(14, 45, 0, 0);

      const trip4Arrival = new Date(today);
      trip4Arrival.setDate(today.getDate() + 30);
      trip4Arrival.setHours(7, 0, 0, 0);
      const trip4Departure = new Date(trip4Arrival);
      trip4Departure.setDate(trip4Arrival.getDate() + 2);
      trip4Departure.setHours(19, 15, 0, 0);

      const sampleData = [
        "Tail Number\tArrival Date\tDeparture Date", // Header row
        `N123AB\t${formatDate(trip1Arrival)}\t${formatDate(trip1Departure)}`,
        `N456CD\t${formatDate(trip2Arrival)}\t${formatDate(trip2Departure)}`,
        `N789EF\t${formatDate(trip3Arrival)}\t${formatDate(trip3Departure)}`,
        `N111GH\t${formatDate(trip4Arrival)}\t${formatDate(trip4Departure)}`,
      ].join("\n");

      const blob = new Blob([sampleData], {
        type: "text/tab-separated-values",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "sample_trips.tsv";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    };

    // Helper function to check if a trip has validation errors
    const getTripValidationError = (trip: Trip): boolean => {
      // Check for missing required fields
      if (!trip.tail_number || !trip.arrival_date || !trip.departure_date) {
        return true;
      }

      // Check for invalid dates (arrival >= departure) - skip if departure is infinity
      if (!isInfinityDate(trip.departure_date)) {
        if (new Date(trip.arrival_date) >= new Date(trip.departure_date)) {
          return true;
        }
      }

      // Check for missing aircraft
      if (!trip.aircraft_id) {
        return true;
      }

      return false;
    };

    // Auto-sort trips with errors to the top
    // Create a stable reference that only changes when trip content changes (not order)
    const tripsContentKey = React.useMemo(() => {
      return trips
        .map(
          (t) =>
            `${t.id}-${t.tail_number}-${t.arrival_date}-${t.departure_date}-${t.aircraft_id}`
        )
        .sort()
        .join("|");
    }, [trips]);

    React.useEffect(() => {
      setTrips((prev) => {
        const tripsWithErrors: Trip[] = [];
        const tripsWithoutErrors: Trip[] = [];

        prev.forEach((trip) => {
          if (getTripValidationError(trip)) {
            tripsWithErrors.push(trip);
          } else {
            tripsWithoutErrors.push(trip);
          }
        });

        // Only update if the order actually changed
        const newOrder = [...tripsWithErrors, ...tripsWithoutErrors];
        const orderChanged = prev.some(
          (trip, index) => trip.id !== newOrder[index]?.id
        );

        return orderChanged ? newOrder : prev;
      });
    }, [tripsContentKey]);

    const addRow = () => {
      setTrips((prev) => [
        ...prev,
        {
          id: Date.now(),
          tail_number: "",
          arrival_date: "",
          departure_date: "",
        },
      ]);
    };

    const removeRow = (id: number) => {
      setTrips((prev) => prev.filter((trip) => trip.id !== id));
    };

    const updateTrip = (id: number, field: keyof Trip, value: string) => {
      setTrips((prev) =>
        prev.map((trip) =>
          trip.id === id ? { ...trip, [field]: value } : trip
        )
      );
    };

    // Update trip with aircraft selection from autocomplete
    const updateTripAircraft = (id: number, aircraft: any) => {
      setTrips((prev) =>
        prev.map((trip) =>
          trip.id === id
            ? {
                ...trip,
                aircraft_id: aircraft?.id,
                make: aircraft?.make,
                model: aircraft?.model,
                wingspan: aircraft?.wingspan,
                length: aircraft?.length,
              }
            : trip
        )
      );
    };

    const handleSave = async () => {
      // Filter out trips with missing data or invalid dates (arrival >= departure)
      const validTrips = trips.filter((t) => {
        if (!t.tail_number || !t.arrival_date || !t.departure_date)
          return false;
        // Check that arrival is before departure - skip validation if departure is infinity
        if (isInfinityDate(t.departure_date)) {
          return true; // Infinity is always valid
        }
        return new Date(t.arrival_date) < new Date(t.departure_date);
      });

      if (!activeFBO?.id) {
        setError("No active FBO selected");
        return;
      }

      if (validTrips.length === 0) {
        setError("No valid trips to save");
        return;
      }

      // Check for trips without aircraft_id - CRITICAL: don't allow upload
      const tripsWithoutAircraft = validTrips.filter((t) => !t.aircraft_id);
      if (tripsWithoutAircraft.length > 0) {
        const tailNumbers = tripsWithoutAircraft
          .map((t) => t.tail_number)
          .join(", ");
        setError(
          `Cannot upload trips without aircraft data. Please select aircraft for: ${tailNumbers}`
        );
        return;
      }

      setLoading(true);
      setError(null);
      setUploadProgress({
        current: 0,
        total: validTrips.length,
        currentItem: "",
      });

      try {
        let successCount = 0;
        const errors: string[] = [];
        const failedTripIds = new Set<number>(); // Track which trip IDs failed

        // Process trips in batches of 5 for parallel execution
        const BATCH_SIZE = 5;
        const batches: Trip[][] = [];
        for (let i = 0; i < validTrips.length; i += BATCH_SIZE) {
          batches.push(validTrips.slice(i, i + BATCH_SIZE));
        }

        // Process each batch in parallel
        for (const batch of batches) {
          const batchResults = await Promise.all(
            batch.map(async (trip) => {
              try {
                // 1. Find or create tenant for this tail number
                let tenantId: string;

                // Check if tenant already exists
                const { data: existingTenants, error: tenantSearchError } =
                  await postgrest
                    .from("tenant")
                    .select("id, aircraft_id")
                    .eq("fbo_id", activeFBO.id)
                    .eq("tail_number", trip.tail_number.trim())
                    .limit(1);

                if (tenantSearchError) {
                  throw new Error(
                    `Failed to search for tenant: ${tenantSearchError.message}`
                  );
                }

                if (existingTenants && existingTenants.length > 0) {
                  // Use existing tenant and ALWAYS update aircraft_id
                  // This fixes bug where NULL aircraft_id tenants were reused without update
                  const existingTenant = existingTenants[0];
                  tenantId = existingTenant.id;

                  // Validate we have aircraft_id (should be caught by validation earlier)
                  if (!trip.aircraft_id) {
                    throw new Error(
                      `Internal error: trip missing aircraft_id for ${trip.tail_number} - should be caught by validation`
                    );
                  }

                  // Always update aircraft_id to ensure it's set (fixes NULL aircraft_id bug)
                  await postgrest
                    .from("tenant")
                    .update({ aircraft_id: trip.aircraft_id })
                    .eq("id", tenantId);
                } else {
                  // Create new tenant with aircraft_id
                  const { data: newTenant, error: tenantError } =
                    await postgrest
                      .from("tenant")
                      .insert({
                        fbo_id: activeFBO.id,
                        tail_number: trip.tail_number.trim(),
                        aircraft_id: trip.aircraft_id, // Include aircraft_id
                        type: TenantType.TRANSIENT, // Default to transient
                      })
                      .select()
                      .single();

                  if (tenantError) {
                    throw new Error(
                      `Failed to create tenant: ${tenantError.message}`
                    );
                  }

                  tenantId = newTenant.id;
                }

                // 2. Create the trip
                const tripData = {
                  tenant_id: tenantId,
                  fbo_id: activeFBO.id,
                  arrival_date: new Date(trip.arrival_date).toISOString(),
                  // Handle infinity departure (base tenants)
                  departure_date: isInfinityDate(trip.departure_date)
                    ? trip.departure_date
                    : new Date(trip.departure_date).toISOString(),
                  status: "scheduled" as const,
                };

                const createdTrip = await tripService.createTrip(tripData);

                // 3. If auto-assignment is enabled, assign trip to location
                if (
                  autoAssignLocationId &&
                  autoAssignLocationType &&
                  createdTrip.id
                ) {
                  try {
                    await placementService.assignToLocationWithGapFilling(
                      createdTrip.id,
                      autoAssignLocationId,
                      autoAssignLocationType,
                      new Date(trip.arrival_date).toISOString(),
                      // Handle infinity departure (base tenants)
                      isInfinityDate(trip.departure_date)
                        ? trip.departure_date
                        : new Date(trip.departure_date).toISOString(),
                      true, // excludeConflicts
                      { x: null, y: null, angle: null } // No specific position initially
                    );
                  } catch (assignmentError: any) {
                    // Trip created but assignment failed - still count as partial success
                    console.error(
                      `Failed to assign trip for ${trip.tail_number}:`,
                      assignmentError
                    );
                    return {
                      success: true,
                      partialSuccess: true,
                      trip,
                      error: `${
                        trip.tail_number
                      }: Trip created but assignment failed - ${
                        assignmentError.message || "Unknown error"
                      }`,
                    };
                  }
                }

                return { success: true, trip };
              } catch (tripError: any) {
                console.error(
                  `Failed to create trip for ${trip.tail_number}:`,
                  tripError
                );
                return {
                  success: false,
                  trip,
                  error: `${trip.tail_number}: ${
                    tripError.message || "Unknown error"
                  }`,
                };
              }
            })
          );

          // Process batch results
          for (const result of batchResults) {
            if (result.success) {
              successCount++;
              if (result.partialSuccess && result.error) {
                errors.push(result.error);
              }
            } else {
              failedTripIds.add(result.trip.id);
              errors.push(result.error);
            }
          }

          // Update progress after each batch
          setUploadProgress({
            current: successCount + errors.length,
            total: validTrips.length,
            currentItem: batch[batch.length - 1].tail_number,
          });
        }

        // Remove successful trips, keep only failed ones and trips with validation errors
        if (successCount > 0) {
          setTrips((prev) => {
            const remainingTrips = prev.filter(
              (t) =>
                // Keep trips that failed during save
                failedTripIds.has(t.id) ||
                // Keep trips that weren't included in validTrips (have validation errors)
                !validTrips.some((vt) => vt.id === t.id)
            );

            // If all trips succeeded, reset to one empty row
            if (remainingTrips.length === 0) {
              return [
                {
                  id: Date.now(),
                  tail_number: "",
                  arrival_date: "",
                  departure_date: "",
                },
              ];
            }

            return remainingTrips;
          });
        }

        // Show results - DO NOT close dialog, keep it open to show results
        const locationMessage = autoAssignLocationName
          ? ` to ${autoAssignLocationName}`
          : "";

        if (successCount === validTrips.length) {
          // All succeeded
          setError(null);
          onComplete(successCount);
          // Set success message (we'll add this to the UI)
          setError(
            `✓ Successfully added ${successCount} trip${
              successCount > 1 ? "s" : ""
            }${locationMessage}`
          );
        } else if (successCount > 0) {
          // Some succeeded, some failed
          setError(
            `✓ Successfully added ${successCount} out of ${
              validTrips.length
            } trips${locationMessage}.\n\n✗ Failed ${
              errors.length
            }:\n${errors.join("\n")}`
          );
          onComplete(successCount);
        } else {
          // All failed
          setError(`✗ Failed to add any trips:\n${errors.join("\n")}`);
        }
      } catch (err: any) {
        console.error("Bulk upload failed:", err);
        setError(err.message || "Failed to save trips. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    const handleClose = () => {
      setTrips([
        { id: 1, tail_number: "", arrival_date: "", departure_date: "" },
      ]);
      setPasteData("");
      setError(null);
      setLoading(false);
      onClose();
    };

    // Count valid trips (has required fields AND arrival < departure)
    const validCount = trips.filter((t) => {
      if (!t.tail_number || !t.arrival_date || !t.departure_date) return false;
      // Allow infinity dates
      if (isInfinityDate(t.departure_date)) return true;
      return new Date(t.arrival_date) < new Date(t.departure_date);
    }).length;

    // Calculate form validity - all trips must have aircraft_id AND valid dates
    const isFormValid = React.useMemo(() => {
      const validTrips = trips.filter((t) => {
        if (!t.tail_number || !t.arrival_date || !t.departure_date)
          return false;
        // Check that arrival is before departure - skip validation if departure is infinity
        if (isInfinityDate(t.departure_date)) return true;
        return new Date(t.arrival_date) < new Date(t.departure_date);
      });
      if (validTrips.length === 0) return false;
      return validTrips.every((t) => t.aircraft_id);
    }, [trips]);

    // Store onStateChange in a ref to avoid recreating the effect
    const onStateChangeRef = React.useRef(onStateChange);
    React.useEffect(() => {
      onStateChangeRef.current = onStateChange;
    }, [onStateChange]);

    // Notify parent of state changes
    React.useEffect(() => {
      onStateChangeRef.current?.(isFormValid, loading);
    }, [isFormValid, loading]);

    // Expose save function via ref
    React.useImperativeHandle(ref, () => ({
      isFormValid,
      loading,
      save: handleSave,
    }));

    return (
      <SimpleBulkUploadPresenter
        open={open}
        onClose={onClose}
        trips={trips}
        pasteData={pasteData}
        setPasteData={setPasteData}
        loading={loading}
        isImporting={isImporting}
        error={error}
        setError={setError}
        validCount={validCount}
        exampleDates={getExampleDates}
        handleImportData={handleImportData}
        downloadSampleFile={downloadSampleFile}
        addRow={addRow}
        removeRow={removeRow}
        updateTrip={updateTrip}
        updateTripAircraft={updateTripAircraft}
        handleSave={handleSave}
        handleClose={handleClose}
        activeFBOId={activeFBO?.id || ""}
        autoAssignLocationName={autoAssignLocationName}
        embedded={embedded}
        uploadProgress={uploadProgress}
      />
    );
  }
);
